// Copyright (c) Docugami, Inc. All rights reserved.

import { SelectionMode } from "@fluentui/react"
import { DgButton } from "components/common/DgCommonStyledComponents"
import { useAppSelector } from "hooks/redux"
import { useCallback, useState } from "react"
import { mapVisibleUserClusters } from "store/state_helpers"
import styled from "styled-components"
import { ChooserList, IChooserItem } from "./ChooserList"
import { ExplorerTextButton } from "./ExplorerStyles"
import { ClusterInfo } from "networking/api"

const ListContainer = styled.div`
    width: 100%;
    overflow: hidden;
    :hover {
        overflow-y: auto;
    }
`

const DocSetChooserContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 20px;
`

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 30px;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
`

interface DocSetChooserProps {
    items: IChooserItem[];
    onSelectItem: (id: string[]) => void;
    onCancel?: () => void;
    selectedKeys?: string[];
    selectionMode: SelectionMode;
}

const DocSetChooser = (props: DocSetChooserProps): JSX.Element => {
    const [selectedIds, setSelectedIds] = useState(props.selectedKeys);

    const onClick = () => {
        if (!!selectedIds) {
            props.onSelectItem(selectedIds);
        }
    }

    return (
        <DocSetChooserContainer>
            <ListContainer>
                <ChooserList items={props.items} selectionMode={props.selectionMode} selectedItems={selectedIds ?? []} onSelectionChanged={setSelectedIds} />
            </ListContainer>
            <ButtonsContainer>
                {!!props.onCancel && <ExplorerTextButton onClick={props.onCancel}>Cancel</ExplorerTextButton>}
                <DgButton onClick={onClick} disabled={!selectedIds?.length}>OK</DgButton>
            </ButtonsContainer>
        </DocSetChooserContainer>
    );
}

const CREATE_KEY = "<<Create New>>";
const UNASSIGN_KEY = "<<Unassign>>"

interface DocSetAssignmentChooserProps {
    onChoose: (id?: string) => void;
    onCreate: () => void;
    onCancel?: () => void;
}

export const DocSetAssignmentChooser = (props: DocSetAssignmentChooserProps) => {
    const userDocSets = useAppSelector(state => mapVisibleUserClusters(state.clusters));

    const getChooserItems = useCallback((): IChooserItem[] => {
        const items = userDocSets.map(set => {
            return {
                id: set.clusterId!,
                label: set.label!
            };
        });

        const unassignItem: IChooserItem = {
            id: UNASSIGN_KEY,
            label: "- Remove From Doc Set"
        };

        const createNewItem: IChooserItem = {
            id: CREATE_KEY,
            label: "+ Create New Doc Set"
        };

        return [createNewItem, unassignItem, ...items];
    }, [userDocSets]);

    function onSelectItem(ids: string[]) {
        if (ids.length !== 1) {
            return;
        }

        const id = ids[0];
        if (id === CREATE_KEY) {
            props.onCreate();
        }
        else if (id === UNASSIGN_KEY) {
            props.onChoose();
        }
        else {
            props.onChoose(id);
        }
    }

    return <DocSetChooser items={getChooserItems()} selectionMode={SelectionMode.single} onSelectItem={onSelectItem} onCancel={props.onCancel} />
}

interface DocSetMergeChooserProps {
    docSets: ClusterInfo[];
    targetDocsetId: string;
    onSelectItem: (ids: string[]) => void;
    onCancel?: () => void;
}

export const DocSetMergeChooser = (props: DocSetMergeChooserProps) => {
    const getChooserItems = useCallback((): IChooserItem[] => {
        return props.docSets.filter(x => x.id !== props.targetDocsetId).map(set => {
            return {
                id: set.clusterId!,
                label: set.label!
            };
        });
    }, [props.docSets, props.targetDocsetId]);

    return <DocSetChooser items={getChooserItems()} selectionMode={SelectionMode.multiple} onSelectItem={props.onSelectItem} onCancel={props.onCancel} />
}